import { AccountReceivablesService, AccountReceivable, WorkflowsService } from '@/api';
import Breadcrumb from '@/components/breadcrumb';
import { useTheme } from '@/components/theme-provider';
import { cn } from '@/lib/utils';
import { useStore } from '@nanostores/react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.min.css';
import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { formatCurrency } from '@/lib/formatters';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { $accountreceivable, createAccountReceivableComment } from '@/stores/accountreceivableStore';
import { WorkflowVisualizer } from './components/WorkflowVisualizer';
import type { WorkflowNode, WorkflowEdge, AccountReceivableStatus } from '@/api/models';
import { Paperclip, X, Circle, Settings, Activity, PhoneCall, Mail, Phone, FileText, MessageSquareDiff, Clock } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { ensureCommentAttachmentUrls } from '@/stores/accountreceivableStore';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { VerticalTimeline, VerticalTimelineElement } from 'react-vertical-timeline-component';
import 'react-vertical-timeline-component/style.min.css';
import { renderStatusBadge } from './components/AccountReceivableListing';
import {
  EllipsisVerticalIcon,
  EyeIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader } from '@googlemaps/js-api-loader';
import { zodResolver } from '@hookform/resolvers/zod';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { CalendarIcon, Save, X as XIcon, AlertCircle, CheckCircle } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
// End additional imports

export type AccountReceivablePageProps = {
  portfolioId: string;
  accountReceivableId: string;
};

interface MarkdownTextProps {
  text: string;
}

type UpdateAccountReceivableResponse = {
  succeeded: boolean;
  message?: string;
};

export const AccountReceivablePage = ({
  portfolioId,
  accountReceivableId,
}: AccountReceivablePageProps) => {
  // State declarations
  const [isOpen, setOpen] = useState(false);
  const [newComment, setNewComment] = useState("");
  const po = useStore($portfolio);
  const org = useStore($org);
  const accountReceivable = useStore($accountreceivable);
  const commentAttachmentUrls = useStore($commentAttachmentUrls);

  const pages = [
    {
      label: org?.name || 'Organizations',
      href: org?.name
        ? `/organization/${org?.id}/portfolios`
        : '/organizations',
    },
    {
      label: po?.portfolio?.name || 'Portfolios',
      href: po?.portfolio?.name
        ? `/organization/${org?.id}/portfolios/${po?.portfolio.id}/accountreceivables`
        : `/organization/${org?.id}/portfolios`,
    },
    {
      label: accountReceivable?.name || 'Account Receivable',
      href: `/organization/${org?.id}/portfolios/${po?.portfolio.id}/accountreceivables/${accountReceivableId}`,
    },
  ];

  const { theme } = useTheme();

  // Query to get account receivable details
  const accountReceivableResult = useQuery({
    queryKey: ['accountReceivableResult', portfolioId, accountReceivableId],
    queryFn: async () => {
      const result = await AccountReceivablesService.getAccountReceivable(accountReceivableId);
      if (!result) throw new Error('No data found');
      if (result.accountReceivable) {
        $accountreceivable.set(result.accountReceivable as AccountReceivable);
      }
      return result;
    },
  });

  const MarkdownText: React.FC<MarkdownTextProps> = ({ text }) => {
    const convertToHTML = (text: string): string => {
      const formattedText = text
        .replace(/\*\*(.*?)\*\*/g, (_, header) => `<h2>${header}</h2>`)
        .replace(/\n/g, '<br/>');
      return formattedText;
    };
    const htmlText = convertToHTML(text);
    return <div dangerouslySetInnerHTML={{ __html: htmlText }} />;
  };

  const formatDate = (utcDate: string) => {
    return new Date(utcDate).toLocaleString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'America/New_York'
    });
  };
  const formatNodeType = (type: any) => {
    const typeMapping = {
      EXECUTION_START: "Execution Start",
      SFTP_UPLOAD: "SFTP Upload",
      SFTP_DOWNLOAD: "SFTP Download",
      DATA_INGESTION: "Data Ingestion",
      ANALYZER: "Analyzer",
      DATA_FLAGGER: "Data Flagger",
      TIMER: "Timer",
      SEND_SMS: "Send SMS",
      EMAIL_NOTIFICATION: "Email Notification",
      AI_OUTBOUND_PHONE_CALL: "AI Outbound Phone Call"
    };

    return typeMapping[type as keyof typeof typeMapping] || type.replace(/_/g, " "); // Fallback to replacing underscores if not mapped
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'bg-green-500';
      case 'overdue': return 'bg-red-500';
      case 'pending': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  // File selection for comments
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(prev => [...prev, ...files]);
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        const previewUrl = URL.createObjectURL(file);
        setPreviews(prev => [...prev, previewUrl]);
      }
    });
  };

  useEffect(() => {
    return () => {
      previews.forEach(preview => URL.revokeObjectURL(preview));
    };
  }, [previews]);

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;
    try {
      setIsUploading(true);
      const response = await createAccountReceivableComment(accountReceivableId, {
        commentText: newComment,
        files: selectedFiles.length > 0 ? selectedFiles : undefined,
      });
      if (response?.succeeded) {
        setNewComment("");
        setSelectedFiles([]);
        setPreviews([]);
      }
    } catch (error) {
      toast.error('Failed to upload files');
    } finally {
      setIsUploading(false);
    }
  };

  const commentsWithImages = useMemo(() => {
    const filtered = accountReceivable?.comments?.filter(comment =>
      comment.attachments?.some(attachment => attachment.mimeType.startsWith('image/'))
    ) ?? [];
    return filtered;
  }, [accountReceivable?.comments]);

  useEffect(() => {
    commentsWithImages.forEach(comment => {
      if (comment.attachments) {
        ensureCommentAttachmentUrls(comment.id, comment.attachments);
      }
    });
  }, [commentsWithImages]);

  const [loadingThumbnails, setLoadingThumbnails] = useState<Record<string, boolean>>({});
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const preloadImage = (url: string, attachmentId: string) => {
    const img = new Image();
    img.onload = () => {
      setLoadedImages(prev => new Set(prev).add(attachmentId));
    };
    img.src = url;
  };

  const [isExpanded, setIsExpanded] = useState(false);
  const [showEventsModal, setShowEventsModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Inline edit form using react-hook-form
  type EditFormValues = {
    name: string;
    loanId: string;
    firstName: string;
    lastName: string;
    middleName?: string;
    contactEmail: string;
    contactPhone: string;
    customerType: 'Individual' | 'Business' | 'Organization';
    balance: string;
    invoiceDate: string;
    dueDate: string;
    amountPaid: string;
    amountDue: string;
    streetAddress1: string;
    streetAddress2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    status: string;
    quickBooksInvoiceLink?: string;
    stripeInvoiceLink?: string;
    customInvoiceLink?: string;
    metadata?: string;
  };

  const { register, handleSubmit, reset, watch, setValue, formState: { errors, isSubmitting } } = useForm<EditFormValues>({
    defaultValues: {
      name: '',
      loanId: '',
      firstName: '',
      lastName: '',
      middleName: '',
      contactEmail: '',
      contactPhone: '',
      customerType: 'Individual',
      balance: '0',
      invoiceDate: '',
      dueDate: '',
      amountPaid: '0',
      amountDue: '0',
      streetAddress1: '',
      streetAddress2: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      status: 'Unpaid',
      quickBooksInvoiceLink: '',
      stripeInvoiceLink: '',
      customInvoiceLink: '',
    }
  });

  useEffect(() => {
    if (isEditing && accountReceivableResult.data?.accountReceivable) {
      const ar = accountReceivableResult.data.accountReceivable;
      reset({
        name: ar.name || '',
        loanId: ar.loanId || '',
        firstName: ar.customer.firstName || '',
        lastName: ar.customer.lastName || '',
        middleName: ar.customer.middleName || '',
        contactEmail: ar.customer.contactEmail || '',
        contactPhone: ar.customer.contactPhone || '',
        customerType: ar.customer.customerType || 'Individual',
        balance: ar.balance.toString() || '0',
        invoiceDate: ar.invoiceDate ? ar.invoiceDate.split('T')[0] : '',
        dueDate: ar.dueDate ? ar.dueDate.split('T')[0] : '',
        amountPaid: ar.amountPaid.toString() || '0',
        amountDue: ar.amountDue.toString() || '0',
        streetAddress1: ar.customer.streetAddress || '',
        streetAddress2: ar.customer.streetAddress2 || '',
        city: ar.customer.city || '',
        state: ar.customer.state || '',
        postalCode: ar.customer.postalCode || '',
        country: ar.customer.country || '',
        status: ar.status || 'Unpaid',
        quickBooksInvoiceLink: ar.quickBooksInvoiceLink || '',
        stripeInvoiceLink: ar.stripeInvoiceLink || '',
        customInvoiceLink: ar.customInvoiceLink || '',
        metadata: ar.metadata ? JSON.stringify(ar.metadata, null, 2) : '',
      });
    }
  }, [isEditing, accountReceivableResult.data, reset]);

  const queryClient = useQueryClient();
  const updateMutation = useMutation<UpdateAccountReceivableResponse, Error, EditFormValues, unknown>({
    mutationFn: async (values: EditFormValues) => {
      const updatedData: any = {
        ...values,
        balance: parseFloat(values.balance),
        amountPaid: parseFloat(values.amountPaid),
        amountDue: parseFloat(values.amountDue),
        invoiceDate: new Date(values.invoiceDate).toISOString(),
        dueDate: new Date(values.dueDate).toISOString(),
        status: values.status as AccountReceivableStatus,
      };

      if (values.metadata) {
        try {
          updatedData.metadata = JSON.parse(values.metadata);
        } catch (e) {
          throw new Error("Invalid JSON in Additional Data field.");
        }
      } else {
        updatedData.metadata = null;
      }
      return await AccountReceivablesService.updateAccountReceivable(accountReceivableId, updatedData);
    },
    onSuccess: (result: any) => {
      if (result.succeeded && result.accountReceivable) {
        toast.success('Account receivable updated successfully');
        // Update stores immediately
        $accountreceivable.set(result.accountReceivable as AccountReceivable);
        const currentList = $accountreceivables.get();
        if (!currentList?.items) return;
        const index = currentList.items.findIndex((ar: AccountReceivable) => ar.id === accountReceivableId);
        if (index !== -1) {
          currentList.items[index] = result.accountReceivable as AccountReceivable;
          $accountreceivables.set(currentList);
        }
        queryClient.invalidateQueries({ queryKey: ['accountReceivableResult', portfolioId, accountReceivableId] });
        queryClient.invalidateQueries({ queryKey: ['accountReceivablesList', portfolioId] });
        setIsEditing(false);
      } else {
        toast.error(result.message || 'Failed to update account receivable');
      }
    },
    onError: () => {
      toast.error('An error occurred while updating the account receivable');
    },
  });
  const markAsPaidMutation = useMutation<UpdateAccountReceivableResponse, Error, void, unknown>({
    mutationFn: async () => {
      return await AccountReceivablesService.updateAccountReceivable(accountReceivableId, {
        status: "Paid",
      });
    },
    onSuccess: (result: any) => {
      if (result.succeeded && result.accountReceivable) {
        toast.success("Account marked as Paid");

        // Update account receivable detail store
        $accountreceivable.set(result.accountReceivable as AccountReceivable);

        // Update account receivables list store
        const currentList = $accountreceivables.get();
        if (!currentList?.items) return;
        const index = currentList.items.findIndex((ar: AccountReceivable) => ar.id === accountReceivableId);
        if (index !== -1) {
          currentList.items[index] = result.accountReceivable as AccountReceivable;
          $accountreceivables.set(currentList);
        }

        // Invalidate queries for both detail and listing pages
        queryClient.invalidateQueries({ queryKey: ["accountReceivableResult", portfolioId, accountReceivableId] });
        queryClient.invalidateQueries({ queryKey: ["accountReceivablesList", portfolioId] });
      } else {
        toast.error(result.message || "Failed to mark as Paid");
      }
    },
    onError: () => {
      toast.error("Error updating status");
    },
  });
  const balance = watch("balance");
  const amountPaid = watch("amountPaid");

  useEffect(() => {
    const b = parseFloat(balance) || 0;
    const p = parseFloat(amountPaid) || 0;
    const newAmountDue = b - p;
    setValue("amountDue", newAmountDue.toString());
  }, [balance, amountPaid, setValue]);


  const handleRun = async (action: 'skip' | 'cancel' | 'pause', workFlowId: string) => {
    try {
      toast.success(`${action.charAt(0).toUpperCase() + action.slice(1)}ing workflow execution...`);

      // Replace with actual API calls based on action
      if (action === 'skip') {
        await WorkflowsService.manualSkipNodeBulkWorkflowExecution(workFlowId, {
          accountReceivableIds: [accountReceivableId]
        });
      } else if (action === 'cancel') {
        await WorkflowsService.manualCancelBulkWorkflowExecution(workFlowId, {
          accountReceivableIds: [accountReceivableId]
        });
      } else if (action === 'pause') {
        await WorkflowsService.manualPauseBulkWorkflowExecution(workFlowId, {
          accountReceivableIds: [accountReceivableId]
        });
      }

    } catch (error) {
      toast.error(`Failed to ${action} workflow execution`);
      console.error(error);
    }
  };

  const watchStatus = watch("status");

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 p-6 space-y-6">      {/* Breadcrumb */}
      <h3 className="m-0 flex-1 p-0">
        <Breadcrumb pages={pages} />
      </h3>
      {/* Page Header */}
      <div className="flex items-center justify-between">
        {isEditing ? (
          <>
            <h2 className="text-3xl font-bold tracking-tight">Edit Account</h2>
            <div className="flex items-center space-x-2">
              <Button type="submit" form="editForm" disabled={isSubmitting || updateMutation.isPending}>
                {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Changes
              </Button>
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <>
            <div>
              {/* Invoice ID */}
              <h2 className="text-3xl font-bold tracking-tight break-all">
                {accountReceivable?.loanId ? `Invoice #INV-${accountReceivable.loanId}` : 'Account Details'}
              </h2>
              {/* Created On Date */}
              {accountReceivable?.createdAtUtc && (
                <p className="text-muted-foreground mt-1 ">
                  Created on {formatDate(accountReceivable.createdAtUtc)}
                </p>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <div className="transform scale-110 pr-2">
                {renderStatusBadge(accountReceivableResult.data?.accountReceivable?.status || 'pending')}
              </div>
              <Button onClick={() => setIsEditing(true)}>Edit</Button>
            </div>
          </>
        )}
      </div>

      {isEditing ? (
        <form id="editForm" onSubmit={handleSubmit((values) => updateMutation.mutate(values))} className="space-y-6 p-4 rounded-md shadow">
          {/* --- Inline edit form fields (same as your existing code) --- */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Client</label>
              <Input {...register("name", { required: "Name is required" })} placeholder="Enter Client name" />
              {errors.name && <p className="text-xs text-red-600">{errors.name.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Invoice ID</label>
              <Input {...register("loanId")} placeholder="Enter invoice ID" />
            </div>
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <Tabs className="mt-2 w-full" value={watchStatus} onValueChange={(value) => setValue("status", value)}>
                <TabsList className="flex flex-wrap gap-1 bg-gray-50 p-1 rounded-lg h-auto">
                  <TabsTrigger value="Unpaid" className="flex-1 bg-white data-[state=active]:bg-gray-100 data-[state=active]:text-gray-900 h-8">Unpaid</TabsTrigger>
                  <TabsTrigger value="PartiallyPaid" className="flex-1 bg-white data-[state=active]:bg-yellow-100 data-[state=active]:text-yellow-900 h-8">Partial</TabsTrigger>
                  <TabsTrigger value="Paid" className="flex-1 bg-white data-[state=active]:bg-green-100 data-[state=active]:text-green-900 h-8">Paid</TabsTrigger>
                  <TabsTrigger value="Overdue" className="flex-1 bg-white data-[state=active]:bg-red-100 data-[state=active]:text-red-900 h-8">Overdue</TabsTrigger>
                  <TabsTrigger value="Cancelled" className="flex-1 bg-white data-[state=active]:bg-gray-100 data-[state=active]:text-gray-900 h-8">Cancelled</TabsTrigger>
                  <TabsTrigger value="VoluntaryChurned" className="flex-1 bg-white data-[state=active]:bg-orange-100 data-[state=active]:text-orange-900 h-8">Vol. Churned</TabsTrigger>
                  <TabsTrigger value="InvoluntaryChurned" className="flex-1 bg-white data-[state=active]:bg-orange-100 data-[state=active]:text-orange-900 h-8">Invol. Churned</TabsTrigger>
                  <TabsTrigger value="VoluntaryChurnedRecovered" className="flex-1 bg-white data-[state=active]:bg-green-100 data-[state=active]:text-green-900 h-8">Vol. Recovered</TabsTrigger>
                  <TabsTrigger value="InvoluntaryChurnedRecovered" className="flex-1 bg-white data-[state=active]:bg-green-100 data-[state=active]:text-green-900 h-8">Invol. Recovered</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Payment Link</label>
              {accountReceivableResult.data?.accountReceivable.accountReceivableDataMedium === 'Quickbooks' ? (
                <Input {...register("quickBooksInvoiceLink")} placeholder="Quickbooks Payment Link" />
              ) : accountReceivableResult.data?.accountReceivable.accountReceivableDataMedium === 'Stripe' ? (
                <Input {...register("stripeInvoiceLink")} placeholder="Stripe Payment Link" />
              ) : (
                <>
                  <div className="space-y-2">
                    <Input {...register("stripeInvoiceLink")} placeholder="Stripe Payment Link" className="mb-2" />
                    <Input {...register("quickBooksInvoiceLink")} placeholder="QuickBooks Payment Link" className="mb-2" />
                    <Input {...register("customInvoiceLink")} placeholder="Custom Payment Link" />
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">First Name</label>
              <Input {...register("firstName")} placeholder="First Name" />
              {errors.firstName && <p className="text-xs text-red-600">{errors.firstName.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Last Name</label>
              <Input {...register("lastName")} placeholder="Last Name" />
              {errors.lastName && <p className="text-xs text-red-600">{errors.lastName.message}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Contact Email</label>
              <Input {...register("contactEmail")} placeholder="Email" />
              {errors.contactEmail && <p className="text-xs text-red-600">{errors.contactEmail.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Contact Phone</label>
              <Input {...register("contactPhone"
                // , { required: "Phone is required" }
              )} placeholder="Phone" />
              {errors.contactPhone && <p className="text-xs text-red-600">{errors.contactPhone.message}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Balance</label>
              <Input type="number" {...register("balance"
                // , { required: "Balance is required" }
              )} placeholder="Balance" />
              {errors.balance && <p className="text-xs text-red-600">{errors.balance.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Invoice Date</label>
              <Input type="date" {...register("invoiceDate"
                // , { required: "Invoice date is required" }
              )} />
              {errors.invoiceDate && <p className="text-xs text-red-600">{errors.invoiceDate.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Due Date</label>
              <Input type="date" {...register("dueDate"
                // , { required: "Due date is required" }
              )} />
              {errors.dueDate && <p className="text-xs text-red-600">{errors.dueDate.message}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Amount Paid</label>
              <Input type="number" {...register("amountPaid"
                // , { required: "Amount paid is required" }
              )} placeholder="Amount Paid" />
              {errors.amountPaid && <p className="text-xs text-red-600">{errors.amountPaid.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Amount Due</label>
              <Input
                {...register("amountDue")}
                placeholder="Amount Due"
                readOnly // Makes the field non-editable
              />
              {errors.amountDue && <p className="text-xs text-red-600">{errors.amountDue.message}</p>}
            </div>

          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-medium">Customer Address</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Street Address 1</label>
                <Input {...register("streetAddress1"
                  // , { required: "Street address is required" }
                )} placeholder="Street Address" />
                {errors.streetAddress1 && <p className="text-xs text-red-600">{errors.streetAddress1.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Street Address 2</label>
                <Input {...register("streetAddress2")} placeholder="Street Address 2" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">City</label>
                <Input {...register("city"
                  // , { required: "City is required" }
                )} placeholder="City" />
                {errors.city && <p className="text-xs text-red-600">{errors.city.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">State</label>
                <Input {...register("state"
                  // , { required: "State is required" }
                )} placeholder="State" />
                {errors.state && <p className="text-xs text-red-600">{errors.state.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Postal Code</label>
                <Input {...register("postalCode"
                  // , { required: "Postal code is required" }
                )} placeholder="Postal Code" />
                {errors.postalCode && <p className="text-xs text-red-600">{errors.postalCode.message}</p>}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Country</label>
              <Input {...register("country"
                // , { required: "Country is required" }
              )} placeholder="Country" />
              {errors.country && <p className="text-xs text-red-600">{errors.country.message}</p>}
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-medium">Additional Data (JSON)</h3>
            <Textarea
              {...register("metadata")}
              placeholder="Enter additional data as JSON (optional)"
              rows={6}
              className="font-mono text-sm"
            />
            {errors.metadata && <p className="text-xs text-red-600">{errors.metadata.message}</p>}
          </div>
        </form>
      ) : (
        // Display View: Two-Column Layout
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Customer Information Card */}
            <Card>
              <CardHeader>
                <CardTitle>Customer Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Amount & Client Name */}
                <div className="grid grid-cols-2 gap-6 items-center">
                  <div>
                    <p className="text-sm text-muted-foreground">Amount</p>
                    <p className="text-2xl font-bold text-green-600">
                      {org?.name === "Sanaam" ? formatCurrency(accountReceivableResult.data?.accountReceivable.balance, "NGN") : formatCurrency(accountReceivableResult.data?.accountReceivable.balance, "USD")}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Client</p>
                    <p className="text-xl break-all font-bold">
                      {accountReceivableResult.data?.accountReceivable.name}
                    </p>
                  </div>
                </div>

                <Separator />
                {/* First Name & Last Name */}
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-muted-foreground">First Name</p>
                    <p className="text-lg break-all font-medium">
                      {accountReceivableResult.data?.accountReceivable.customer.firstName}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Last Name</p>
                    <p className="text-lg break-all font-medium">
                      {accountReceivableResult.data?.accountReceivable.customer.lastName}
                    </p>
                  </div>
                </div>

                <Separator />

                {/* Issue Date & Due Date */}
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-muted-foreground">Issue Date</p>
                    <p className="text-lg font-medium">
                      {accountReceivableResult.data?.accountReceivable.invoiceDate &&
                        formatDate(accountReceivableResult.data.accountReceivable.invoiceDate)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Due Date</p>
                    <p className="text-lg font-medium">
                      {accountReceivableResult.data?.accountReceivable.dueDate &&
                        formatDate(accountReceivableResult.data.accountReceivable.dueDate)}
                    </p>
                  </div>
                </div>

                <Separator />

                {/* Contact Info */}
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="text-lg font-medium flex items-center">
                      <Mail className="h-4 w-4 mr-2  text-muted-foreground" />
                      {accountReceivableResult.data?.accountReceivable.customer.contactEmail}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <p className="text-lg font-medium flex items-center">
                      <Phone className="h-4 w-4 mr-2 text-muted-foreground" />
                      {accountReceivableResult.data?.accountReceivable.customer.contactPhone}
                    </p>
                  </div>
                </div>

                <Separator />

                {/* Address */}
                <div>
                  <p className="text-sm text-muted-foreground">Address</p>
                  <p className="text-lg break-all font-medium">
                    {accountReceivableResult.data?.accountReceivable.customer.streetAddress}
                    {accountReceivableResult.data?.accountReceivable.customer.streetAddress2 &&
                      `, ${accountReceivableResult.data.accountReceivable.customer.streetAddress2}`}
                  </p>
                  <p className="text-lg break-all font-medium">
                    {`${accountReceivableResult.data?.accountReceivable.customer.city}, ${accountReceivableResult.data?.accountReceivable.customer.state} ${accountReceivableResult.data?.accountReceivable.customer.postalCode}`}
                  </p>
                  <p className="text-lg break-all font-medium">
                    {accountReceivableResult.data?.accountReceivable.customer.country}
                  </p>
                </div>

                <Separator />

                {/* Action Buttons */}
                {!accountReceivableResult.isLoading && (
                  <div className="flex flex-wrap gap-4 mt-2">
                    {/* Mark as Paid Button */}
                    {accountReceivableResult.data?.accountReceivable?.status.toLowerCase() !== "Paid" && (
                      <Button
                        className="bg-green-500 text-white"
                        variant="outline"
                        onClick={() => markAsPaidMutation.mutate()}
                        disabled={markAsPaidMutation.isPending}
                      >
                        {markAsPaidMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <CheckCircleIcon className="h-5 w-5 mr-2" />
                        )}
                        Mark as Paid
                      </Button>
                    )}

                    {/* Call Client Button */}
                    {accountReceivableResult.data?.accountReceivable.customer.contactPhone && (
                      <>
                        <Button
                          className="text-black hover:text-blue-600"
                          variant="outline"
                          onClick={() =>
                            window.location.href = `tel:${accountReceivableResult.data.accountReceivable.customer.contactPhone}`
                          }
                        >
                          <PhoneCall className="h-5 w-5 mr-2" />
                          Call Client
                        </Button>
                        {/* Send SMS Button */}
                        <Button
                          className=" text-black hover:text-blue-600"
                          variant="outline"
                          onClick={() =>
                            window.location.href = `sms:${accountReceivableResult.data.accountReceivable.customer.contactPhone}`
                          }
                        >
                          <MessageSquareDiff className="h-5 w-5 mr-2" />
                          Send SMS
                        </Button>
                      </>
                    )}

                    {/* Send Email Button */}
                    {accountReceivableResult.data?.accountReceivable.customer.contactEmail && (
                      <Button
                        className="text-black hover:text-blue-600"
                        variant="outline"
                        onClick={() =>
                          window.location.href = `mailto:${accountReceivableResult.data.accountReceivable.customer.contactEmail}`
                        }
                      >
                        <Mail className="h-5 w-5 mr-2" />
                        Send Email
                      </Button>
                    )}
                  </div>
                )}

              </CardContent>
            </Card>


            {/* Events Card */}
            {/* <Card onClick={() => setShowEventsModal(true)} className="cursor-pointer hover:bg-accent/50 transition-colors">
              <CardHeader>
                <CardTitle>Events</CardTitle>
              </CardHeader>
              <CardContent>
                {accountReceivableResult.data?.events && accountReceivableResult.data.events.length > 0 ? (
                  <div className="text-sm text-muted-foreground space-y-2">
                    {accountReceivableResult.data.events.slice(0, 3).map(event => (
                      <div key={event.eventId}>
                        {event.eventType} - {formatDate(event.eventTimestamp)}
                      </div>
                    ))}
                    {accountReceivableResult.data.events.length > 3 && (
                      <div className="text-sm italic">
                        Click to see full timeline ({accountReceivableResult.data.events.length} events)
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No events</p>
                )}
              </CardContent>
            </Card> */}



            {/* Comments Section */}
            <Card>
              <CardHeader>
                <CardTitle>Comments</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {accountReceivable?.comments?.length ? (
                  <div className="space-y-4">
                    {[...accountReceivable.comments]
                      .sort((a, b) => new Date(a.createdAtUtc).getTime() - new Date(b.createdAtUtc).getTime())
                      .map(comment => (
                        <div key={comment.id} className="p-4 rounded-lg bg-muted">
                          <div className="flex justify-between items-start mb-2">
                            <div className="font-medium">{comment.profile?.name}</div>
                            <div className="text-sm text-muted-foreground">
                              {formatDate(comment.createdAtUtc)}
                            </div>
                          </div>
                          <p className="text-sm break-all">{comment.commentText}</p>
                          {comment.attachments && comment.attachments.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {comment.attachments.map(attachment => {
                                const signedUrls = commentAttachmentUrls[comment.id]?.[attachment.id];
                                return (
                                  <div key={attachment.id} className="relative">
                                    {attachment.mimeType.startsWith('image/') ? (
                                      <a href={signedUrls?.url || '#'} target="_blank" rel="noopener noreferrer" className="block">
                                        {!signedUrls?.thumbnailUrl ? (
                                          <div className="h-20 w-20 bg-muted animate-pulse rounded-md" />
                                        ) : (
                                          <>
                                            {!loadedImages.has(attachment.id) && (
                                              <div className="h-20 w-20 bg-muted animate-pulse rounded-md absolute" />
                                            )}
                                            <img
                                              src={signedUrls.thumbnailUrl}
                                              alt={attachment.fileName}
                                              className={`h-20 w-20 object-cover rounded-md transition-opacity duration-200 ${loadedImages.has(attachment.id) ? 'opacity-100' : 'opacity-0'}`}
                                              onLoad={() => {
                                                if (signedUrls.thumbnailUrl) {
                                                  preloadImage(signedUrls.thumbnailUrl, attachment.id);
                                                }
                                              }}
                                              loading="eager"
                                            />
                                          </>
                                        )}
                                      </a>
                                    ) : (
                                      <a href={signedUrls?.url || '#'} target="_blank" rel="noopener noreferrer" className="p-2 border rounded-md flex items-center gap-2 hover:bg-accent">
                                        <Paperclip className="h-4 w-4" />
                                        <span className="text-sm truncate max-w-[100px]">{attachment.fileName}</span>
                                      </a>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-muted-foreground">No comments yet</div>
                )}
                {selectedFiles.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-2 border rounded-md">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="relative group">
                        {file.type.startsWith('image/') ? (
                          <div className="relative w-20 h-20">
                            <img src={previews[index]} alt={file.name} className="w-full h-full object-cover rounded-md" />
                            <Button
                              variant="destructive"
                              size="icon"
                              className="absolute -top-2 -right-2 h-5 w-5 opacity-0 group-hover:opacity-100"
                              onClick={() => {
                                setSelectedFiles(files => files.filter((_, i) => i !== index));
                                setPreviews(prevs => prevs.filter((_, i) => i !== index));
                              }}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <div className="p-2 border rounded-md flex items-center gap-2">
                            <Paperclip className="h-4 w-4" />
                            <span className="text-sm truncate max-w-[100px]">{file.name}</span>
                            <Button
                              variant="destructive"
                              size="icon"
                              className="h-5 w-5 opacity-0 group-hover:opacity-100"
                              onClick={() => {
                                setSelectedFiles(files => files.filter((_, i) => i !== index));
                              }}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 pt-4">
                  <Input
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="flex-1"
                  />
                  <Button variant="outline" size="icon" type="button" onClick={() => document.getElementById('file-upload')?.click()}>
                    <Paperclip className="h-4 w-4" />
                  </Button>
                  <input type="file" id="file-upload" className="hidden" onChange={handleFileSelect} accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp" multiple />
                  <Button onClick={handleSubmitComment} disabled={!newComment.trim() || isUploading}>
                    {isUploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Save
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
          {/* Right Column */}
          <div className="space-y-6">
            {/* Financial Summary Card */}
            <Card>
              <CardHeader>
                <CardTitle>Financial Summary</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Total Balance</p>
                  <p className="text-2xl font-bold">
                    {org?.name === "Sanaam" ? formatCurrency(accountReceivableResult.data?.accountReceivable.balance, "NGN") : formatCurrency(accountReceivableResult.data?.accountReceivable.balance, "USD")}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Amount Paid</p>
                  <p className="text-2xl font-bold text-green-600">
                    {org?.name === "Sanaam" ? formatCurrency(accountReceivableResult.data?.accountReceivable.amountPaid, "NGN") : formatCurrency(accountReceivableResult.data?.accountReceivable.amountPaid, "USD")}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Amount Due</p>
                  <p className="text-2xl font-bold text-red-600">
                    {org?.name === "Sanaam" ? formatCurrency(accountReceivableResult.data?.accountReceivable.amountDue, "NGN") : formatCurrency(accountReceivableResult.data?.accountReceivable.amountDue, "USD")}
                  </p>
                </div>
                <div className="space-y-1 col-span-3">
                  <p className="text-sm text-muted-foreground">Payment Link</p>
                  <p className="text-lg break-all font-medium max-w-full overflow-hidden overflow-ellipsis whitespace-normal">
                    {accountReceivableResult.data?.accountReceivable.quickBooksInvoiceLink || 
                     accountReceivableResult.data?.accountReceivable.stripeInvoiceLink || 
                     accountReceivableResult.data?.accountReceivable.customInvoiceLink || 
                     <span className="text-gray-400">No Payment link available</span>}
                  </p>
                </div>
                <div className="space-y-1 col-span-3">
                  <p className="text-sm text-muted-foreground">Stripe Product</p>
                  <p className="text-lg break-all font-medium max-w-full overflow-hidden overflow-ellipsis whitespace-normal">
                    {accountReceivableResult.data?.accountReceivable.stripeProductId} ({accountReceivableResult.data?.accountReceivable.stripeProductName})
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Additional Data Card */}
            

            {/* Additional Data Card */}
            {!isEditing && accountReceivableResult.data?.accountReceivable?.metadata && Object.keys(accountReceivableResult.data.accountReceivable.metadata).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Additional Data</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(accountReceivableResult.data.accountReceivable.metadata).map(([key, value]) => (
                    <div key={key} className="space-y-1">
                      <p className="text-sm text-muted-foreground">{key}</p>
                      <p className="text-lg font-medium break-all">{String(value)}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
            {!isEditing && (!accountReceivableResult.data?.accountReceivable?.metadata || Object.keys(accountReceivableResult.data.accountReceivable.metadata).length === 0) && (
              <Card>
                <CardHeader>
                  <CardTitle>Additional Data</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">No additional data available.</p>
                </CardContent>
              </Card>
            )}

            {/* Activity Timeline Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>Activity Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                {accountReceivableResult.isLoading ? (
                  <p className="text-center text-muted-foreground">Loading activity...</p>
                ) : !accountReceivableResult.data?.accountReceivable?.invoiceDate &&
                  !(accountReceivableResult.data?.events?.length) ? (
                  <p className="text-center text-muted-foreground">No activity recorded</p>
                ) : (
                  <div className="relative">
                    {/* Vertical line on the left */}
                    <div className="absolute left-3 top-0 bottom-0 w-px bg-gray-200"></div>

                    <div className="space-y-6">
                      {/* Invoice Created */}
                      {accountReceivableResult.data?.accountReceivable?.invoiceDate && (
                        <div className="relative pl-10">
                          {/* Icon circle */}
                          <div className="absolute left-0 top-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                            <FileText className="h-3 w-3 text-blue-600" />
                          </div>
                          <p className="text-sm font-medium">Invoice Created</p>
                          <p className="text-xs text-gray-500">
                            {formatDate(accountReceivableResult.data.accountReceivable.invoiceDate)}
                          </p>
                        </div>
                      )}

                      {/* Map over any other timeline events */}
                      {accountReceivableResult.data?.events?.map((event) => {
                        // Provide defaults or switch logic based on event type
                        let icon = <AlertCircle className="h-3 w-3 text-gray-600" />;
                        let bgColor = "bg-gray-100";
                        let title = event.eventType || "Unknown Event";
                        const dateString = event.eventTimestamp
                          ? formatDate(event.eventTimestamp)
                          : "N/A";

                        // Example icon logic
                        if (event.eventType === "ReminderSent") {
                          icon = <Mail className="h-3 w-3 text-yellow-600" />;
                          bgColor = "bg-yellow-100";
                          title = "Reminder Sent";
                        } else if (event.eventType === "Payment") {
                          icon = <CheckCircle className="h-3 w-3 text-green-600" />;
                          bgColor = "bg-green-100";
                          title = "Payment Received";
                        } else if (event.eventType === "Overdue") {
                          icon = <AlertCircle className="h-3 w-3 text-red-600" />;
                          bgColor = "bg-red-100";
                          title = "Invoice Overdue";
                        }

                        return (
                          <div key={event.eventId} className="relative pl-10">
                            <div
                              className={`absolute left-0 top-1 w-6 h-6 rounded-full ${bgColor} flex items-center justify-center`}
                            >
                              {icon}
                            </div>
                            <p className="text-sm font-medium">{title}</p>
                            <p className="text-xs text-gray-500">{dateString}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Assigned Workflows Card */}
            <Card>
              <CardHeader>
                <CardTitle>Assigned Workflows</CardTitle>
              </CardHeader>
              <CardContent>
                {accountReceivableResult.data?.workflows?.length ? (
                  accountReceivableResult.data.workflows.map((workflow) => {
                    // Sort nodes by 'order' or default to array order
                    const nodes = (workflow.nodes ?? []).sort(
                      (a, b) => (a.order ?? 0) - (b.order ?? 0)
                    );

                    return (
                      <div key={workflow.id} className="mb-6">
                        <h3 className="text-lg font-semibold mb-2">{workflow.name}</h3>

                        {/* Outer container with relative positioning for the single vertical line */}
                        <div className="relative">
                          {/* Single vertical line behind all circles */}
                          <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-300" />

                          {/* Render each node */}
                          {nodes.map((node) => {
                            const isActive =
                              node.id ===
                              workflow.executions?.[0]?.executionData?.lastExecutedNodeId;
                            // Determine if the workflow is completed
                            const isCompleted = workflow.executions?.[0]?.status === 'Completed';

                            return (
                              <div key={node.id} className="flex items-center mb-6">
                                {/* Circle on top of the line */}
                                <div
                                  className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center ${isActive ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'
                                    }`}
                                  style={{ marginLeft: '0.25rem' }}
                                >
                                  {/* Show a check mark for completed workflow */}
                                  {isCompleted ? (
                                    <CheckCircle className="h-4 w-4" />
                                  ) : (
                                    // Show clock icon for non-completed workflow
                                    <Clock className="h-4 w-4" />
                                  )}
                                </div>

                                {/* Node name */}
                                <p className="ml-4 text-sm font-medium">
                                  {formatNodeType(node.type)}
                                  {isActive && ' (Current)'}
                                  {isActive && node.type === 'TIMER' && (
                                    <div className="flex gap-2 mt-1">
                                      <Button size="sm" variant="outline" className="px-2 py-1 h-7 text-xs" onClick={(e) => {
                                        e.preventDefault();
                                        if (workflow?.id) handleRun('skip', workflow.id);
                                      }}>
                                        Skip
                                      </Button>
                                      <Button size="sm" variant="outline" className="px-2 py-1 h-7 text-xs" onClick={(e) => {
                                        e.preventDefault();
                                        if (workflow?.id) handleRun('cancel', workflow.id);
                                      }}>
                                        Cancel
                                      </Button>
                                      <Button size="sm" variant="outline" className="px-2 py-1 h-7 text-xs" onClick={(e) => {
                                        e.preventDefault();
                                        if (workflow?.id) handleRun('pause', workflow.id);
                                      }}>
                                        Pause
                                      </Button>
                                    </div>
                                  )}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-muted-foreground">No workflows assigned</p>
                )}
              </CardContent>
            </Card>






          </div>
        </div>
      )}

      {/* Additional Data Card */}
      

      <Dialog open={showEventsModal} onOpenChange={(open) => setShowEventsModal(open)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Event Timeline</DialogTitle>
          </DialogHeader>
          {accountReceivableResult.data?.events && accountReceivableResult.data.events.length > 0 ? (
            <VerticalTimeline lineColor="#e5e7eb">
              {accountReceivableResult.data.events.map(event => (
                <VerticalTimelineElement
                  key={event.eventId}
                  className="vertical-timeline-element"
                  contentStyle={{ background: 'var(--background)', borderRadius: '0.5rem' }}
                  contentArrowStyle={{ borderRight: '7px solid var(--background)' }}
                  date={formatDate(event.eventTimestamp)}
                  iconStyle={{ background: 'var(--primary)', color: '#fff' }}
                  icon={<Circle className="h-4 w-4" />}
                >
                  <h3 className="font-semibold">{event.eventType}</h3>
                </VerticalTimelineElement>
              ))}
            </VerticalTimeline>
          ) : (
            <div className="text-center py-6 text-muted-foreground">No events found</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};